import type { ReactNode } from 'react';
import { useStore } from '@/state/store';
import type { MixChannel } from '@/audio/levels';
import { LevelFader, MixerSoundControl } from './MixerControls';
import type { SoundMenuSearch, SoundOption } from './soundMenu';

/** A channel's instrument as a dropdown on its strip. */
export interface MixerSoundPicker {
  /** The menu's name ("Comp sound"). */
  label: string;
  value: string;
  options: readonly SoundOption[];
  onChange: (value: string) => void;
  /** A search field over a long list (the drum loops). */
  search?: SoundMenuSearch;
  /** A pick leaves the menu open (the drum loops). */
  keepOpen?: boolean;
}

export interface MixerChannel {
  id: MixChannel;
  label: string;
  sound: string;
  /** Present where the instrument can be changed here; absent, the name is plain text. */
  picker?: MixerSoundPicker;
  enabled: boolean;
  available: boolean;
  unavailableReason: string;
  varies?: boolean;
  onEnabledChange: (enabled: boolean) => void;
}

/** An inline group within the Band or Mixer panel. */
export function MixerSettings({
  channel,
  children,
}: {
  channel: MixChannel | 'master' | 'click';
  children: ReactNode;
}) {
  return (
    <section
      className="mixer-settings-content"
      data-mixer-settings={channel}
      aria-label={`${channel === 'master' ? 'Master effects' : channel[0].toUpperCase() + channel.slice(1)} settings`}
    >
      {channel === 'master' && <h3>Master effects</h3>}
      {children}
    </section>
  );
}

/** Band exposes instruments and playing; Mixer exposes balance and effects. */
export function BandMixer({
  panel,
  channels,
  source,
  scope,
  lineup,
  playingSettings,
  effects,
}: {
  panel: 'band' | 'mixer';
  channels: MixerChannel[];
  source?: ReactNode;
  scope?: ReactNode;
  lineup: ReactNode;
  playingSettings: ReactNode;
  effects: ReactNode;
}) {
  const solo = useStore((s) => s.soloChannel);
  const setSolo = useStore((s) => s.setSoloChannel);
  if (panel === 'band')
    return (
      <div className="mixer band-mixer band-playing">
        {source}
        {scope && (
          <div className="band-mixer-scope">
            <span className="label">Playing settings</span>
            {scope}
          </div>
        )}
        <div className="band-mixer-lineup">{lineup}</div>
        <div className="band-mixer-settings">{playingSettings}</div>
      </div>
    );
  return (
    <div className="mixer band-mixer">
      {scope && (
        <div className="band-mixer-scope">
          <span className="label">Channel mutes</span>
          {scope}
        </div>
      )}
      <div className="band-mixer-overview">
        <div className="band-mixer-bank" role="group" aria-label="Mix levels">
          {/* No status line under Mute and Solo (user, Oct 2026: "remove the
              text at the bottom"): the buttons' own pressed states say it, and
              an unavailable channel says why on hover. */}
          {channels.map((channel) => {
            return (
              <div
                className="band-mixer-strip"
                key={channel.id}
                data-channel={channel.id}
                title={channel.available ? undefined : channel.unavailableReason}
              >
                <div className="band-mixer-channel-head">
                  <div className="band-mixer-channel">
                    <strong>{channel.label}</strong>
                  </div>
                  {channel.picker && channel.available ? (
                    <MixerSoundControl
                      compact
                      sound={channel.sound}
                      label={channel.picker.label}
                      value={channel.picker.value}
                      options={channel.picker.options}
                      onChange={channel.picker.onChange}
                      search={channel.picker.search}
                      keepOpen={channel.picker.keepOpen}
                      testid={`mixer-sound-${channel.id}`}
                    />
                  ) : (
                    <span className="band-mixer-sound-unavailable" title={channel.sound}>
                      <span>{channel.sound}</span>
                    </span>
                  )}
                </div>
                <LevelFader fader={channel.id} label={channel.label} vertical />
                <button
                  type="button"
                  className="band-mixer-mute"
                  aria-label={`Mute ${channel.label.toLowerCase()}`}
                  aria-pressed={channel.varies ? 'mixed' : !channel.enabled}
                  disabled={!channel.available}
                  data-testid={`mute-${channel.id}`}
                  onClick={() => channel.onEnabledChange(channel.varies ? false : !channel.enabled)}
                >
                  {channel.varies || channel.enabled ? 'Mute' : 'Unmute'}
                </button>
                <button
                  type="button"
                  className="mixer-solo"
                  aria-label={`Solo ${channel.label.toLowerCase()}`}
                  aria-pressed={solo === channel.id}
                  disabled={!channel.available && solo !== channel.id}
                  data-testid={`solo-${channel.id}`}
                  onClick={() => setSolo(channel.id)}
                >
                  Solo
                </button>
              </div>
            );
          })}
          <div className="band-mixer-strip band-mixer-master" data-testid="master-channel">
            <div className="band-mixer-channel">
              <strong>Master</strong>
            </div>
            <LevelFader fader="master" label="Master" vertical />
          </div>
        </div>
      </div>
      <div className="band-mixer-settings">{effects}</div>
    </div>
  );
}
